import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown as ChevronDownIcon, Search } from "lucide-react";
import Label from "components/crm/ui/Label";
import { FIELD_BOX_CLASS, FIELD_GAP_CLASS } from "components/crm/ui/fieldStyles";
import useCloseOnEscape from "components/crm/ui/useCloseOnEscape";
import useText from "hooks/useText";

const MENU_ROOM = 200;
const folded = (value) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// The menu is absolute, so a scrolling container (a table) clips it: open it
// upwards when the room below runs out and there is more above.
const opensUpwards = (element) => {
  if (!element) return false;
  let scroller = element.parentElement;
  while (scroller && !/(auto|scroll|hidden)/.test(getComputedStyle(scroller).overflowY)) {
    scroller = scroller.parentElement;
  }
  const box = scroller?.getBoundingClientRect();
  const top = Math.max(box?.top ?? 0, 0);
  const bottom = Math.min(box?.bottom ?? window.innerHeight, window.innerHeight);
  const rect = element.getBoundingClientRect();
  const below = bottom - rect.bottom;
  return below < MENU_ROOM && rect.top - top > below;
};

// Dropdown with rich options (icon, label, description). `compact`: plain
// rows (a menu of days), the icon on the field only. `dense`: low rows, the
// icon kept but small and unframed (a list of people and their avatars).
// `searchable`: a search at the top of the list, on the labels (a long one).
const RichSelect = ({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
  hint,
  renderValue,
  compact = false,
  dense = false,
  searchable = false,
}) => {
  const { text } = useText("crmUi");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [upwards, setUpwards] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    // Each opening starts from the whole list.
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useCloseOnEscape(open, () => setOpen(false));
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);
  const shown = searchable && query.trim()
    ? options.filter((o) => folded(o.label).includes(folded(query.trim())))
    : options;
  const current = options.find((o) => o.value === value);
  return (
    <div ref={ref} className={`relative flex flex-col ${FIELD_GAP_CLASS}`}>
      {label && <Label htmlFor={id}>{label}</Label>}
      {/* The button and its list: the list hangs right under (or above) the
          button, whatever comes before it. */}
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => {
            if (!open) setUpwards(opensUpwards(ref.current));
            setOpen((o) => !o);
          }}
          className={`flex w-full items-center gap-2 ${FIELD_BOX_CLASS} text-left ${
            open ? "border-primary" : "border-lineStrong"
          }`}
        >
          {current?.icon && (
            <current.icon size={18} className={current.iconClass ?? "text-ink"} />
          )}
          <span
            className={`min-w-0 flex-1 truncate ${
              current ? "text-ink" : "text-faint"
            }`}
          >
            {current
              ? renderValue
                ? renderValue(current)
                : current.label
              : placeholder ?? text("choose")}
          </span>
          <ChevronDownIcon
            size={18}
            className={`text-faint transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
        {open && (
          <div
            className={`absolute left-0 right-0 z-30 max-h-80 overflow-y-auto rounded-xl border border-line bg-white p-1.5 shadow-pop ${
              upwards ? "bottom-full mb-1" : "top-full mt-1"
            }`}
          >
            {searchable && (
              <div className="mb-1 flex items-center gap-2 border-b border-line px-2 pb-2 pt-1">
                <Search size={16} className="shrink-0 text-faint" />
                <input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Rechercher"
                  aria-label="Rechercher"
                  className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-faint"
                />
              </div>
            )}
            {searchable && !shown.length && (
              <p className="px-3 py-2 text-sm text-muted">Aucun résultat</p>
            )}
            {shown.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-3 rounded-lg px-3 text-left hover:bg-surfaceMuted ${
                  compact || dense ? "py-2" : "py-2.5"
                } ${o.value === value ? "bg-gray-50" : ""}`}
              >
                {o.icon && dense && <o.icon size={18} className={o.iconClass ?? "text-ink"} />}
                {o.icon && !compact && !dense && (
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-line bg-white">
                    <o.icon size={18} className={o.iconClass ?? "text-ink"} />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-sm text-ink ${compact || dense ? "font-normal" : "font-medium"}`}>
                    {o.label}
                  </span>
                  {o.description && (
                    <span className="block truncate text-xs text-muted">
                      {o.description}
                    </span>
                  )}
                </span>
                {o.value === value && (
                  <Check size={18} className="text-primary" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      {hint && <p className="-mt-1 text-xs text-faint">{hint}</p>}
    </div>
  );
};

export default RichSelect;
